// Phase 44 — PRICE-02 · D-07 · D-09 · D-11 — Canvas Pricing section.
//
// Extracted from CanvasFormSections.tsx (Phase 43 SectionPricing) into a
// dedicated file so the market-band "Suggest price" button + pre-apply chip
// + post-apply ProvenanceChip mutual-exclusion matrix can be wired cleanly
// into the `our_price` field's label row.
//
// Mutual exclusion matrix (D-07 + UI-SPEC §Component Placement):
//   State A — "Suggest price" AiActionButton   (no market-band run yet, not applied)
//   State B — <SuggestedPriceChip>             (market-band result, not applied, not dismissed)
//   State C — <ProvenanceChip source='AI'>     (post-apply — Phase 43 primitive takes over)
//
// Transitions are genuine unmount→mount (NOT variant swaps on a single
// component) per UI-SPEC Hand-off Note #3. The parent conditionally
// renders exactly ONE of the three based on:
//   - useMarketBand state (result / dismissed / isActive)
//   - useProvenance('our_price').state.entry.source === 'AI'
//
// Threat model refs:
//   T-44-05 — price-field tampering via popover. The chip's inline Apply
//   button has event.stopPropagation() (enforced in SuggestedPriceChip.tsx).
//   Apply writes go through react-hook-form — no window.* side channel.
//
// Typography lock (UI-SPEC rev 2): 13px `text-[0.8125rem]` labels at weight
// 400, 14px body inputs. NO text-xs, NO font-medium on new Phase 44 code.

import { Sparkles, Tag } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '@marine/ui/card';
import { Input } from '@marine/ui/input';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@marine/ui/form';
import type { WizardFormData } from '@/modules/inventory/components/boat-form/types/wizard.types';
import type { UseMarketBandReturn } from '@/modules/inventory/hooks/useMarketBand';
import { useAIComparables } from '@/modules/inventory/hooks/useAIComparables';
import { AiActionButton } from '@/modules/inventory/components/ai/AiActionButton';
import { FieldTintWrapper } from '../FieldTintWrapper';
import { ProvenanceChip } from '../ProvenanceChip';
import { SuggestedPriceChip } from '../SuggestedPriceChip';
import { useProvenance } from '../hooks/useProvenance';
import type { ProvenanceEntry } from '@/modules/inventory/types/boat.types';
import { useCan } from '@/modules/tenant/hooks/useTenantAuth';

// =============================================================================
// FieldRow — duplicated shape from CanvasFormSections so the non-price rows in
// Pricing render identically. Kept local-only; the original remains the source
// of truth for the other 8 sections.
// =============================================================================

type WizardFieldName = keyof WizardFormData;

interface FieldRowProps {
  fieldName: WizardFieldName;
  label: string;
  type?: 'text' | 'number';
  placeholder?: string;
  required?: boolean;
}

function FieldRow({
  fieldName,
  label,
  type = 'text',
  placeholder,
  required = false,
}: FieldRowProps) {
  const { control } = useFormContext<WizardFormData>();
  const { state, clear } = useProvenance(fieldName);

  return (
    <FieldTintWrapper fieldName={fieldName}>
      <FormField
        control={control}
        name={fieldName as never}
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between gap-2">
              <FormLabel className="text-xs">
                {label}
                {required && <span className="text-destructive ml-0.5">*</span>}
              </FormLabel>
              <ProvenanceChip
                fieldName={fieldName}
                entry={state.entry}
                onClear={clear}
              />
            </div>
            <FormControl>
              <Input
                type={type}
                placeholder={placeholder}
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  if (type === 'number') {
                    const v = e.target.value;
                    field.onChange(v === '' ? null : Number(v));
                  } else {
                    field.onChange(e);
                  }
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </FieldTintWrapper>
  );
}

// =============================================================================
// OurPriceRow — the special price row that hosts the market-band chip.
// marketBand is INJECTED from PricingSection (lifted to LiveFillCanvas) —
// Phase 44 carryover Task 2: the hook is called exactly once at the top of
// the component tree so both the "Suggest price" button AND the
// post-Comparables CTA share the same job/result state.
// =============================================================================

interface OurPriceRowProps {
  boatId: string | null;
  /** Required — supplied by PricingSection which receives it from the parent. */
  marketBand: UseMarketBandReturn;
}

function OurPriceRow({ boatId, marketBand }: OurPriceRowProps) {
  const { control } = useFormContext<WizardFormData>();
  const { state, clear } = useProvenance('our_price');

  // Comparables hook reused for the CTA variant's [Run Comparables] action.
  // useAIComparables requires a non-null boatId; we only dispatch when we
  // have one (Pitfall #8 — market-band requires a persisted row for stored
  // comps lookup).
  const comparables = useAIComparables(boatId ?? '');

  // Provenance state drives the mutual-exclusion matrix. `source === 'AI'`
  // covers the post-apply state — the Phase 43 ProvenanceChip renders in
  // place of SuggestedPriceChip without a component-level variant swap.
  const provenanceEntry =
    state.entry && !state.isConflict ? (state.entry as ProvenanceEntry) : null;
  const alreadyApplied = provenanceEntry?.source === 'AI';

  const showChip =
    marketBand.result !== null && !marketBand.dismissed && !alreadyApplied;
  const showButton =
    !marketBand.result && !marketBand.dismissed && !alreadyApplied;

  return (
    <FieldTintWrapper fieldName="our_price">
      <FormField
        control={control}
        name={'our_price' as never}
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between gap-2">
              <FormLabel className="text-xs">Our price</FormLabel>

              <div className="flex items-center gap-2">
                {/* State A — explicit "Suggest price" button (D-07). Disabled
                    when no boatId (Pitfall #8 — market-band needs a persisted
                    row). Tooltip explains why. */}
                {showButton && (
                  <AiActionButton
                    label="Suggest price"
                    loadingLabel="Suggesting…"
                    icon={Sparkles}
                    onClick={() => marketBand.trigger.mutate()}
                    isLoading={
                      marketBand.trigger.isPending || marketBand.isActive
                    }
                    disabled={!boatId}
                    disabledReason={
                      !boatId
                        ? 'Save draft first to suggest price'
                        : undefined
                    }
                  />
                )}

                {/* State B — pre-apply SuggestedPriceChip. Mutual-exclusive with
                    the Suggest button above and the ProvenanceChip below. */}
                {showChip && marketBand.result && (
                  <SuggestedPriceChip
                    suggestedPrice={marketBand.result.suggestedPrice}
                    reasoning={marketBand.result.reasoning}
                    confidence={marketBand.result.confidence}
                    sourceComps={marketBand.result.sourceComps}
                    onApply={marketBand.apply}
                    onDismiss={marketBand.dismiss}
                    onRunComparables={() => comparables.trigger.mutate()}
                  />
                )}

                {/* State C — post-apply ProvenanceChip (Phase 43 primitive
                    inherited unchanged). Renders for ANY source — AI from the
                    apply flow, or manual/URL/VIN/PDF if the dealer later
                    changes source via the chip's own controls. */}
                {alreadyApplied && (
                  <ProvenanceChip
                    fieldName="our_price"
                    entry={state.entry}
                    onClear={clear}
                  />
                )}
              </div>
            </div>
            <FormControl>
              <Input
                type="number"
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  field.onChange(v === '' ? null : Number(v));
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </FieldTintWrapper>
  );
}

// =============================================================================
// PricingSection — composer.
// =============================================================================

export interface PricingSectionProps {
  /**
   * Persisted inventory_item_id. When null, the "Suggest price" button is
   * disabled with a tooltip "Save draft first to suggest price" (D-07 +
   * Pitfall #8 — market-band requires a persisted row for stored comps
   * lookup).
   */
  boatId: string | null;
  /**
   * Phase 44 carryover Task 2 — the shared useMarketBand instance lifted to
   * LiveFillCanvas. Required so EXACTLY ONE call site exists across the canvas
   * (the CTA-triggered run and the explicit "Suggest price" button share the
   * same job/result state). Callers MUST pass this; a missing value is a type
   * error, not a silent undefined.
   */
  marketBand: UseMarketBandReturn;
}

/**
 * Phase 44 — PRICE-02 · D-07 · D-09 · D-11 — canvas Pricing section.
 *
 * Renders the 8 pricing fields per Phase 43 (our_price, msrp, regular_price,
 * sale_price, cost_price, invoice, rigging, freight) with the market-band
 * chip integration on `our_price` only. The four cost fields render only for
 * finance.cost.view holders (ACL-09).
 */
export function PricingSection({ boatId, marketBand }: PricingSectionProps) {
  // ACL-09 (#1408, D-08/D-09): the cost inputs are mounted only for
  // finance.cost.view holders, so RHF never registers them for anyone else
  // and a stale cached payload cannot surface a cost figure. No Restricted
  // marker replaces them (D-11).
  const canViewCost = useCan('finance.cost.view');

  // Phase 45.5.3 (CANVAS-14) — wrapped in the v1 Card (Tag icon header) to match
  // the other sections. The `id="pricing"` stays on the outer <section> so the
  // scroll-spy IntersectionObserver target survives the Card-wrapping.
  return (
    <section id="pricing">
      <Card>
        <CardHeader className="min-h-[34px] bg-accent/50">
          <div className="flex items-center gap-2">
            <Tag className="h-4 w-4 text-primary" />
            <CardTitle className="text-2sm">Pricing</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* 2-column field grid mirroring the Specs section (SectionSpecs). */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
            <OurPriceRow boatId={boatId} marketBand={marketBand} />
            <FieldRow fieldName="msrp" label="MSRP" type="number" />
            <FieldRow fieldName="regular_price" label="Regular price" type="number" />
            <FieldRow fieldName="sale_price" label="Sale price" type="number" />
            {canViewCost && (
              <>
                <FieldRow fieldName="cost_price" label="Cost price" type="number" />
                <FieldRow fieldName="invoice_amount" label="Invoice" type="number" />
                <FieldRow fieldName="rigging" label="Rigging" type="number" />
                <FieldRow fieldName="freight" label="Freight" type="number" />
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
